import { useState } from "react";
import Swal from "sweetalert2";
import { adminPost, readImageSize } from "./adminApi";

const VALID_EXT = ["jpg", "jpeg", "png"];
const MAX_MB = 5;

// field key === themesdata target (the backend uses it as the multipart field name)
const FIELDS = [
  { key: "themeImage", label: "Upload Thumbnail", size: [200, 300], urlKey: "themeImageUrl" },
  { key: "logo", label: "Upload Logo", size: [400, 300], urlKey: "logoUrl" },
  { key: "background_desk", label: "Upload Desktop Background", size: [1920, 1080], urlKey: "backgroundDeskUrl" },
  { key: "background_mob", label: "Upload Mobile Background", size: [414, 896], urlKey: "backgroundMobUrl" },
];

// Create (all 4 images required) / edit (images optional) dialog — ports the two modals of themes.php
export default function ThemeModal({ mode, theme, session, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(isEdit ? theme.newthemeName || theme.themeName || "" : "");
  const [description, setDescription] = useState(isEdit ? theme.themeDescription || "" : "");
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);

  const fail = (title) => Swal.fire({ icon: "error", title });

  const validateFile = async (field, file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!VALID_EXT.includes(ext)) return "Unsupported file format";
    if (file.size > MAX_MB * 1024 * 1024) return `Filesize exceeds max limit ${MAX_MB} MB`;
    try {
      const [w, h] = await readImageSize(file);
      if (w !== field.size[0] || h !== field.size[1]) {
        return `Incorrect Dimensions of ${field.label.replace("Upload ", "")} (expected ${field.size[0]}x${field.size[1]})`;
      }
    } catch (err) {
      return err.message;
    }
    return null;
  };

  const onPick = async (field, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const error = await validateFile(field, file);
    if (error) {
      e.target.value = "";
      setFiles((f) => ({ ...f, [field.key]: undefined }));
      fail(error);
      return;
    }
    setFiles((f) => ({ ...f, [field.key]: file }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    if (!trimmedName || !trimmedDesc) {
      fail("Theme Name and Theme Details cannot be empty or spaces only");
      return;
    }
    if (!isEdit && FIELDS.some((f) => !files[f.key])) {
      fail("All files are required");
      return;
    }

    const form = new FormData();
    if (isEdit) {
      form.append("themeName", theme.themename); // theme id
      form.append("newThemeName", trimmedName); // display name
    } else {
      form.append("themeName", trimmedName);
    }
    form.append("themeDescription", trimmedDesc);
    FIELDS.forEach((f) => files[f.key] && form.append(f.key, files[f.key]));

    setBusy(true);
    try {
      await adminPost(isEdit ? "updateThemeMedia" : "uploadNewTheme", form, session);
      await Swal.fire({
        icon: "success",
        title: isEdit ? "Theme Updated" : "Theme Created",
        timer: 1200,
        showConfirmButton: false,
      });
      onSaved();
    } catch (err) {
      fail(err.message);
      setBusy(false);
    }
  };

  const previewOf = (f) =>
    files[f.key] ? URL.createObjectURL(files[f.key]) : isEdit ? theme[f.urlKey] : null;

  return (
    <>
      <div className="ut-modal-backdrop" onClick={busy ? undefined : onClose} />
      <form className="ut-modal" onSubmit={submit}>
        <h2 className="ut-title">{isEdit ? "Update Theme" : "Create Theme"}</h2>
        {isEdit && <p className="ut-theme-name">{theme.newthemeName || theme.themeName}</p>}

        <div className="ut-form-group">
          <label>Theme Name</label>
          <input
            type="text"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter theme name"
            required
          />
          <div className="ut-code">* Max length 100 characters.</div>
        </div>

        <div className="ut-form-group">
          <label>Theme Description</label>
          <input
            type="text"
            value={description}
            maxLength={200}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter theme description"
            required
          />
          <div className="ut-code">* Max length 200 characters.</div>
        </div>

        <div className="ut-image-upload-group">
          <div className="ut-file-upload-container">
            {FIELDS.map((f) => {
              const preview = previewOf(f);
              return (
                <div className="ut-file-upload-wrapper" key={f.key}>
                  <label className="ut-label">{f.label.replace("Upload ", "")} ({f.size[0]}x{f.size[1]})</label>
                  <div className="ut-file-icon-container">
                    {preview ? (
                      <img src={preview} alt="" className="ut-thumb-preview" />
                    ) : (
                      <i className="fas fa-upload ut-file-icon" />
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    className="ut-file-upload-input"
                    onChange={(e) => onPick(f, e)}
                  />
                </div>
              );
            })}
          </div>
          <div className="ut-code">
            * Allowed formats: JPG, JPEG, PNG{isEdit ? " (leave empty to keep the current image)" : ""}
          </div>
        </div>

        <div className="ut-modal-actions">
          <button type="button" className="ut-cancel-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="ut-update-btn" disabled={busy}>
            {busy ? "Saving..." : isEdit ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </>
  );
}
